/**
 * First-touch attribution capture.
 *
 * On the visitor's FIRST page load in a session, we record:
 *   - landing_page         (the URL they landed on)
 *   - referrer_url         (document.referrer at that moment)
 *   - utm_source           (from URL params)
 *   - utm_medium
 *   - utm_campaign
 *   - utm_term
 *   - utm_content
 *   - first_visit_ts       (ISO timestamp)
 *
 * The record persists in localStorage under `ib_first_touch_v1` so it survives
 * multi-page journeys within the same browser (e.g., blog → /apply). It's
 * intentionally first-touch only — subsequent visits do NOT overwrite it.
 *
 * When the application form submits, `getAttributionPayload()` returns these
 * fields ready to include in the POST /applications payload.
 */

const STORAGE_KEY = "ib_first_touch_v1";

function readUrlParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get("utm_source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
      utm_term: p.get("utm_term") || "",
      utm_content: p.get("utm_content") || "",
    };
  } catch {
    return { utm_source: "", utm_medium: "", utm_campaign: "", utm_term: "", utm_content: "" };
  }
}

/**
 * Capture first-touch attribution once per browser. Idempotent.
 * Call on app boot (index.js or App.js top-level useEffect).
 */
export function captureFirstTouchAttribution() {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return JSON.parse(existing);
    const record = {
      landing_page: window.location.pathname + window.location.search,
      referrer_url: document.referrer || "",
      first_visit_ts: new Date().toISOString(),
      ...readUrlParams(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    return record;
  } catch {
    // localStorage disabled/full — silently fail; attribution is best-effort
    return null;
  }
}

/**
 * Read the stored first-touch record. Returns an empty object if none exists.
 * Use this to attach attribution fields to any form submission (e.g. /applications).
 */
export function getAttributionPayload() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const record = JSON.parse(raw);
    return {
      landing_page: record.landing_page || "",
      referrer_url: record.referrer_url || "",
      utm_source: record.utm_source || "",
      utm_medium: record.utm_medium || "",
      utm_campaign: record.utm_campaign || "",
      utm_term: record.utm_term || "",
      utm_content: record.utm_content || "",
      first_visit_ts: record.first_visit_ts || "",
    };
  } catch {
    return {};
  }
}
